import React, { useState, useCallback, useMemo, useEffect } from "react";
import {
  Drawer,
  DrawerBody,
  DrawerHeader,
  DrawerOverlay,
  DrawerContent,
  DrawerCloseButton,
  DrawerFooter,
  VStack,
  Text,
  Button,
  Box,
  Flex,
  FormControl,
  FormLabel,
  FormErrorMessage,
  Heading,
  Center,
  Spinner,
  Skeleton,
} from "@chakra-ui/react";
import { Select } from "antd";
import { Formik, Form } from "formik";
import { observer } from "mobx-react-lite";
import { useTranslation } from "react-i18next";
import { MapPin, MapPinned } from "lucide-react";
import { Input } from "../../../components/Input/Input";
import * as Yup from "yup";
import { ILocationMenuItem } from "../../../stores/data/operationRuleLocations/OperationRuleLocationModel";
import { GeofenceMapSelector } from "../../../components/Map";
import { MapPointPreview } from "../../../components/Map/MapPointPreview";
import { LatLngLiteral } from "leaflet";
import { useHistory } from "react-router-dom";
import { useStores } from "../../../stores/setup/use-store";
import { LocationTypeSelector } from "../OperationRuleLocation/Components/LocationTypeSelector";
import { BackButton } from "../../../components/Map/BackButton";

export interface LocationEditFormValues {
  name: string;
  coordinates: LatLngLiteral[];
  locationType: "point" | "area" | null;
  macroRegionId?: number;
  stationRegion?: string;
  regionId?: number;
  stationId?: number;
  savedLocationId?: number;
}

interface LocationEditDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (values: LocationEditFormValues) => void;
  location: ILocationMenuItem | null;
  isLoading?: boolean;
}

export const LocationEditDrawer: React.FC<LocationEditDrawerProps> = observer(
  ({ isOpen, onClose, onSubmit, location, isLoading = false }) => {
    const { t } = useTranslation("translation", {
      keyPrefix: "locations",
    });

    const { t: tAlert } = useTranslation("translation", {
      keyPrefix: "oceanWeatherIntelligence.insightAlert",
    });

    const { t: tOperationRule } = useTranslation("translation", {
      keyPrefix: "oceanWeatherIntelligence.operationRuleLocation",
    });

    const history = useHistory();

    const {
      dataStores: {
        forecastChartPageStore,
        oceanWeatherIntelligenceStore,
        regionStore,
        insightAlertStore,
        operationRuleLocationStore,
        locationStore,
      },
    } = useStores();

    // State for geofence editing
    const [selectedGeofence, setSelectedGeofence] = useState<LatLngLiteral[]>(
      [],
    );

    // State for location type selection (for changing between point/area)
    const [locationType, setLocationType] =
      useState<"point" | "area" | null>(null);

    // State for showing type selection (when user wants to change type)
    const [showTypeSelection, setShowTypeSelection] = useState(false);

    // Ref to track if we already processed the geofence from store (to avoid re-processing after clear)
    const hasProcessedGeofenceRef = React.useRef(false);

    // State to store the processed area geofence (to avoid losing it when store is cleared)
    const [processedAreaGeofence, setProcessedAreaGeofence] =
      useState<LatLngLiteral[] | null>(null);

    // State to signal that form coordinates should be cleared (used when changing type)
    const [shouldClearFormCoordinates, setShouldClearFormCoordinates] =
      useState(false);

    // Determine original location type: point (single coordinate) or area (polygon).
    // Priority: station → inline geofence → saved ForecastLocation reference.
    // The `location` branch is required for events that reference a saved
    // ForecastLocation (the new aurora-ui SaveLocationModal flow persists here
    // rather than inlining the polygon on the OperationRuleLocation).
    const originalLocationType = useMemo((): "point" | "area" | null => {
      if (location?.station) {
        return "point";
      }
      if (location?.geofence && location.geofence.length === 1) {
        return "point";
      }
      if (location?.geofence && location.geofence.length > 1) {
        return "area";
      }
      if (location?.location) {
        if (location.location.coordinatesType === "point") {
          return "point";
        }
        if (location.location.coordinatesType === "area") {
          return "area";
        }
      }
      return null;
    }, [location]);

    // Get initial coordinates from location
    const initialCoordinates = useMemo((): LatLngLiteral[] => {
      if (location?.station) {
        return [{ lat: location.station.lat, lng: location.station.lon }];
      }
      if (location?.geofence) {
        return location.geofence;
      }
      if (location?.location?.coordinates?.length) {
        return location.location.coordinates;
      }
      return [];
    }, [location]);

    // Update states when location changes or drawer opens
    useEffect(() => {
      console.log(
        "[LocationEditDrawer] useEffect state update - isOpen:",
        isOpen,
        "location:",
        location?.id,
        "hasProcessedGeofence:",
        hasProcessedGeofenceRef.current,
      );
      console.log(
        "[LocationEditDrawer] selectedStationForAlert:",
        insightAlertStore.selectedStationForAlert,
      );
      console.log(
        "[LocationEditDrawer] oceanWeatherIntelligenceStore.selectedGeofence:",
        oceanWeatherIntelligenceStore.selectedGeofence,
      );

      if (isOpen && location) {
        // Check if returning from point selection - this takes priority
        if (insightAlertStore.selectedStationForAlert) {
          const station = insightAlertStore.selectedStationForAlert;
          console.log(
            "[LocationEditDrawer] Setting from selectedStationForAlert",
          );
          setSelectedGeofence([
            { lat: station.station_lat, lng: station.station_lon },
          ]);
          setLocationType("point");
          setShowTypeSelection(false);
          setProcessedAreaGeofence(null); // Clear any area geofence
          hasProcessedGeofenceRef.current = true;
          return; // Don't override with original coordinates
        }

        // Check if returning from map selection with new geofence (and we haven't processed it yet)
        if (
          oceanWeatherIntelligenceStore.selectedGeofence?.length > 0 &&
          !hasProcessedGeofenceRef.current
        ) {
          console.log(
            "[LocationEditDrawer] Setting from selectedGeofence (area):",
            oceanWeatherIntelligenceStore.selectedGeofence,
          );
          // Make a copy of the geofence data before clearing
          const geofenceCopy = [
            ...oceanWeatherIntelligenceStore.selectedGeofence,
          ];
          setSelectedGeofence(geofenceCopy);
          setProcessedAreaGeofence(geofenceCopy); // Store for initialValues
          setLocationType("area");
          setShowTypeSelection(false);
          hasProcessedGeofenceRef.current = true;
          // Clear after using
          setTimeout(() => {
            oceanWeatherIntelligenceStore.setSelectedGeofence([]);
          }, 100);
          return; // Don't override with original coordinates
        }

        // If we already processed, don't override
        if (hasProcessedGeofenceRef.current) {
          console.log(
            "[LocationEditDrawer] Already processed geofence, skipping",
          );
          return;
        }

        // Default: use original location data
        console.log(
          "[LocationEditDrawer] Setting from original location data:",
          initialCoordinates,
        );
        setSelectedGeofence(initialCoordinates);
        setLocationType(originalLocationType);
        setShowTypeSelection(false);
        setProcessedAreaGeofence(null);
      }
    }, [
      isOpen,
      location,
      initialCoordinates,
      originalLocationType,
      oceanWeatherIntelligenceStore,
      oceanWeatherIntelligenceStore.selectedGeofence,
      insightAlertStore.selectedStationForAlert,
    ]);

    // Load saved areas when drawer opens
    useEffect(() => {
      if (isOpen) {
        locationStore.getAllLocationsAPI();
      }
    }, [isOpen, locationStore]);

    // Reset states when drawer closes
    useEffect(() => {
      if (!isOpen) {
        setShowTypeSelection(false);
        hasProcessedGeofenceRef.current = false; // Reset for next open
        setProcessedAreaGeofence(null); // Clear processed area geofence
      }
    }, [isOpen]);

    const validationSchema = Yup.object().shape({
      name: Yup.string().required(t("requiredField") as string),
      coordinates: Yup.array()
        .min(1, t("pleaseDrawAnArea") as string)
        .required(t("pleaseDrawAnArea") as string),
    });

    const initialValues: LocationEditFormValues = useMemo(() => {
      // Check if we have a selected station from point selection mode
      const selectedStation = insightAlertStore.selectedStationForAlert;
      console.log(
        "[LocationEditDrawer] initialValues memo - selectedStation:",
        selectedStation,
        "processedAreaGeofence:",
        processedAreaGeofence,
      );

      // PRIORITY 1: Check if we have a processed area geofence (user drew an area)
      if (processedAreaGeofence && processedAreaGeofence.length > 0) {
        const areaValues = {
          name: location?.name || "",
          coordinates: processedAreaGeofence,
          locationType: "area" as const,
          // Clear station data when it's an area
          stationId: undefined,
          regionId: undefined,
          macroRegionId: undefined,
          stationRegion: undefined,
        };
        console.log(
          "[LocationEditDrawer] initialValues from processedAreaGeofence:",
          areaValues,
        );
        return areaValues;
      }

      // PRIORITY 2: Check if we have a selected station from point selection mode
      if (selectedStation) {
        // Find the correct macroRegionId from regionStore based on station's macro_region_origin
        let macroRegionId: number | undefined;
        if (selectedStation.macro_region_origin) {
          const macroRegion = regionStore.macroRegions?.find(
            (region) => region.origin === selectedStation.macro_region_origin,
          );
          macroRegionId = macroRegion?.id;
        }
        // Fallback: try to find by region_id if macro_region_origin is not available
        if (!macroRegionId && selectedStation.region_id) {
          const macroRegion = regionStore.macroRegions?.find(
            (region) => region.id === selectedStation.region_id,
          );
          macroRegionId = macroRegion?.id;
        }
        console.log(
          "[LocationEditDrawer] macroRegionId resolved:",
          macroRegionId,
          "from origin:",
          selectedStation.macro_region_origin,
        );

        const values = {
          name: location?.name || "",
          coordinates: [
            {
              lat: selectedStation.station_lat,
              lng: selectedStation.station_lon,
            },
          ],
          locationType: "point" as const,
          macroRegionId: macroRegionId,
          regionId: selectedStation.region_id,
          stationId: selectedStation.station_id,
          stationRegion: `${selectedStation.station_id}-${selectedStation.region_id}`,
        };
        console.log(
          "[LocationEditDrawer] initialValues from selectedStation:",
          values,
        );
        return values;
      }

      // PRIORITY 3: Check if we have selected geofence from store (before it's cleared)
      if (oceanWeatherIntelligenceStore.selectedGeofence?.length > 0) {
        return {
          name: location?.name || "",
          coordinates: oceanWeatherIntelligenceStore.selectedGeofence,
          locationType: "area" as const,
          stationId: undefined,
          regionId: undefined,
          macroRegionId: undefined,
          stationRegion: undefined,
        };
      }

      // FALLBACK: use original location data
      // macroRegionId comes directly from the menu-list API response (ILocationMenuStation.macroRegionId)
      const fallbackValues = {
        name: location?.name || "",
        coordinates: initialCoordinates,
        locationType: originalLocationType,
        macroRegionId: location?.station?.macroRegionId,
        regionId: location?.station?.regionId,
        stationId: location?.station?.id,
        stationRegion: location?.station
          ? `${location.station.id}-${location.station.regionId}`
          : undefined,
        savedLocationId: location?.location?.id,
      };
      console.log(
        "[LocationEditDrawer] initialValues fallback (no selectedStation):",
        fallbackValues,
      );
      return fallbackValues;
    }, [
      location,
      initialCoordinates,
      originalLocationType,
      oceanWeatherIntelligenceStore.selectedGeofence,
      insightAlertStore.selectedStationForAlert,
      processedAreaGeofence,
      regionStore.macroRegions,
    ]);

    // Handle clearing the geofence
    const handleClearGeofence = useCallback(() => {
      setSelectedGeofence([]);
      insightAlertStore.setSelectedStationForAlert(null);
    }, [insightAlertStore]);

    // Handle area selection from dropdown
    const handleAreaSelection = useCallback(
      (
        selectedAreaId: number,
        setFieldValue: (field: string, value: any) => void,
      ) => {
        const selectedArea = locationStore.areas.find(
          (area) => area.id === selectedAreaId,
        );

        if (selectedArea) {
          setFieldValue("savedLocationId", selectedAreaId);

          if (selectedArea.coordinates) {
            setFieldValue("coordinates", selectedArea.coordinates);
            setSelectedGeofence(selectedArea.coordinates);
          }
        }
      },
      [locationStore.areas],
    );

    // Get title for monitored location section based on type
    const monitoredLocationTitle = useMemo(() => {
      if (locationType === "area") {
        return tAlert("monitoredArea");
      }
      return tAlert("monitoredLocation");
    }, [locationType, tAlert]);

    // Create station object for MapPointPreview
    const stationForPreview = useMemo(() => {
      // Check if user selected a new station
      const selectedStation = insightAlertStore.selectedStationForAlert;
      if (selectedStation) {
        return {
          station_id: selectedStation.station_id,
          station_name: selectedStation.station_name,
          station_lat: selectedStation.station_lat,
          station_lon: selectedStation.station_lon,
          station_depth: 0,
          station_depth_unit: "m",
          region_id: selectedStation.region_id,
        };
      }

      if (location?.station) {
        return {
          station_id: location.station.id,
          station_name: location.station.name,
          station_lat: location.station.lat,
          station_lon: location.station.lon,
          station_depth: 0,
          station_depth_unit: "m",
          region_id: location.station.regionId,
        };
      }
      if (location?.geofence && location.geofence.length === 1) {
        return {
          station_id: 0,
          station_name: location.name,
          station_lat: location.geofence[0].lat,
          station_lon: location.geofence[0].lng,
          station_depth: 0,
          station_depth_unit: "m",
        };
      }
      return null;
    }, [location, insightAlertStore.selectedStationForAlert]);

    // Handle drawing mode for areas - navigate to map for drawing
    const handleDrawingMode = useCallback(
      (formValues?: any) => {
        forecastChartPageStore.setIsAreaDrawingMode(true);

        // Store form values for retrieval after drawing
        operationRuleLocationStore.setEditLocationFormValues({
          id: location?.id,
          name: formValues?.name || location?.name || "",
        });

        // Set existing geofence for editing
        if (selectedGeofence.length > 0) {
          oceanWeatherIntelligenceStore.setSelectedGeofence(selectedGeofence);
        }

        const locationId = location?.id || "new";
        history.push(
          `/forecast-charts?event_location_id=${locationId}&origin=event-manager`,
        );
      },
      [
        forecastChartPageStore,
        operationRuleLocationStore,
        oceanWeatherIntelligenceStore,
        history,
        location,
        selectedGeofence,
      ],
    );

    // Handle edit point - navigate to map for selection
    const handleEditPoint = useCallback(() => {
      forecastChartPageStore.setIsPointSelectionMode(true);

      // Store form values for retrieval after selection
      operationRuleLocationStore.setEditLocationFormValues({
        id: location?.id,
        name: location?.name || "",
      });

      // Set original station if exists
      if (stationForPreview) {
        insightAlertStore.setOriginalStationForAlert(stationForPreview as any);
        insightAlertStore.setSelectedStationForAlert(stationForPreview as any);
      }

      const locationId = location?.id || "new";
      history.push(
        `/forecast-charts?event_location_id=${locationId}&point_selection=true&origin=event-manager`,
      );
    }, [
      forecastChartPageStore,
      operationRuleLocationStore,
      insightAlertStore,
      history,
      location,
      stationForPreview,
    ]);

    // Handle location type selection
    const handleLocationTypeSelect = useCallback(
      (type: "point" | "area") => {
        setLocationType(type);
        setShowTypeSelection(false);
        setShouldClearFormCoordinates(true); // Signal to clear form coordinates

        if (type === "area") {
          // Clear point data when switching to area
          insightAlertStore.setSelectedStationForAlert(null);
          setSelectedGeofence([]); // Clear the geofence (which may contain old point coordinates)
          setProcessedAreaGeofence(null); // Clear any processed area
          hasProcessedGeofenceRef.current = false; // Reset the ref so new geofence can be processed
        } else {
          // Clear area data when switching to point and navigate to map for selection
          setSelectedGeofence([]);
          setProcessedAreaGeofence(null);
          hasProcessedGeofenceRef.current = false;
          insightAlertStore.setSelectedStationForAlert(null);

          // Navigate to map for point selection (like InsightAlert flow)
          forecastChartPageStore.setIsPointSelectionMode(true);
          operationRuleLocationStore.setEditLocationFormValues({
            id: location?.id,
            name: location?.name || "",
          });
          const locationId = location?.id || "new";
          history.push(
            `/forecast-charts?event_location_id=${locationId}&point_selection=true&origin=event-manager`,
          );
        }
      },
      [
        insightAlertStore,
        forecastChartPageStore,
        operationRuleLocationStore,
        history,
        location,
      ],
    );

    // Handle back to type selection
    const handleBackToTypeSelection = useCallback(() => {
      setShowTypeSelection(true);
      setLocationType(null);
      setShouldClearFormCoordinates(true); // Signal to clear form coordinates
      // Clear all selection data when going back to type selection
      setSelectedGeofence([]);
      setProcessedAreaGeofence(null);
      insightAlertStore.setSelectedStationForAlert(null);
      hasProcessedGeofenceRef.current = false;
    }, [insightAlertStore]);

    // Handle close with cleanup
    const handleClose = useCallback(() => {
      insightAlertStore.setSelectedStationForAlert(null);
      insightAlertStore.setOriginalStationForAlert(null);
      oceanWeatherIntelligenceStore.setSelectedGeofence([]);
      operationRuleLocationStore.setEditLocationFormValues(null);
      insightAlertStore.setIsGeofenceInvalid(false);
      onClose();
    }, [
      insightAlertStore,
      oceanWeatherIntelligenceStore,
      operationRuleLocationStore,
      onClose,
    ]);

    // Generate a key based on selected station or area to force Formik remount when selection changes
    const formKey = useMemo(() => {
      const selectedStation = insightAlertStore.selectedStationForAlert;
      let key: string;
      if (processedAreaGeofence && processedAreaGeofence.length > 0) {
        // Area was drawn - use area key
        key = `form-area-${processedAreaGeofence.length}-${
          location?.id || "new"
        }`;
      } else if (selectedStation) {
        key = `form-station-${selectedStation.station_id}-${selectedStation.region_id}`;
      } else {
        key = `form-location-${location?.id || "new"}`;
      }
      console.log(
        "[LocationEditDrawer] formKey computed:",
        key,
        "selectedStation:",
        selectedStation,
        "processedAreaGeofence:",
        processedAreaGeofence?.length,
      );
      return key;
    }, [
      insightAlertStore.selectedStationForAlert,
      location?.id,
      processedAreaGeofence,
    ]);

    return (
      <Drawer isOpen={isOpen} placement="right" onClose={handleClose} size="xl">
        <Formik
          key={formKey}
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={(values) => {
            console.log(
              "[LocationEditDrawer] onSubmit - values before cleanup:",
              JSON.stringify(values, null, 2),
            );
            // Cleanup before submit
            insightAlertStore.setSelectedStationForAlert(null);
            oceanWeatherIntelligenceStore.setSelectedGeofence([]);
            onSubmit(values);
          }}
          enableReinitialize
        >
          {({
            handleSubmit,
            setFieldValue,
            values,
            errors,
            touched,
            handleChange,
          }) => {
            // Sync form values when returning from point selection
            // This ensures the stationId and regionId are properly set
            // eslint-disable-next-line react-hooks/rules-of-hooks
            React.useEffect(() => {
              const selectedStation = insightAlertStore.selectedStationForAlert;
              console.log(
                "[LocationEditDrawer] useEffect sync - selectedStation:",
                selectedStation,
                "isOpen:",
                isOpen,
              );
              if (selectedStation && isOpen) {
                // Find the correct macroRegionId from regionStore
                let macroRegionId: number | undefined;
                if (selectedStation.macro_region_origin) {
                  const macroRegion = regionStore.macroRegions?.find(
                    (region) =>
                      region.origin === selectedStation.macro_region_origin,
                  );
                  macroRegionId = macroRegion?.id;
                }
                if (!macroRegionId && selectedStation.region_id) {
                  const macroRegion = regionStore.macroRegions?.find(
                    (region) => region.id === selectedStation.region_id,
                  );
                  macroRegionId = macroRegion?.id;
                }
                console.log(
                  "[LocationEditDrawer] useEffect - Setting form values with stationId:",
                  selectedStation.station_id,
                  "regionId:",
                  selectedStation.region_id,
                  "macroRegionId:",
                  macroRegionId,
                );
                setFieldValue("coordinates", [
                  {
                    lat: selectedStation.station_lat,
                    lng: selectedStation.station_lon,
                  },
                ]);
                setFieldValue("locationType", "point");
                setFieldValue("stationId", selectedStation.station_id);
                setFieldValue("regionId", selectedStation.region_id);
                setFieldValue("macroRegionId", macroRegionId);
                setFieldValue(
                  "stationRegion",
                  `${selectedStation.station_id}-${selectedStation.region_id}`,
                );
              }
            }, [
              insightAlertStore.selectedStationForAlert,
              isOpen,
              setFieldValue,
            ]);

            // Clear form coordinates when switching between point/area types
            // eslint-disable-next-line react-hooks/rules-of-hooks
            React.useEffect(() => {
              if (shouldClearFormCoordinates) {
                console.log(
                  "[LocationEditDrawer] Clearing form coordinates due to type change",
                );
                setFieldValue("coordinates", []);
                setFieldValue("stationId", undefined);
                setFieldValue("regionId", undefined);
                setFieldValue("macroRegionId", undefined);
                setFieldValue("stationRegion", undefined);
                setShouldClearFormCoordinates(false);
              }
            }, [shouldClearFormCoordinates, setFieldValue]);

            const handleSubmitForm = (e: React.FormEvent) => {
              e.preventDefault();
              handleSubmit();
            };

            // Handle coordinates change from map
            const handleCoordinatesChange = (coordinates: LatLngLiteral[]) => {
              setSelectedGeofence(coordinates);
              setFieldValue("coordinates", coordinates);
            };

            // Handle clear geofence
            const handleClear = () => {
              handleClearGeofence();
              setFieldValue("coordinates", []);
              setFieldValue("stationId", undefined);
              setFieldValue("stationRegion", undefined);
            };

            // Handle drawing mode with form values
            const handleDrawingModeWithFormValues = () => {
              handleDrawingMode({
                name: values.name,
                coordinates: values.coordinates || [],
              });
            };

            // Check if has valid point or area
            const hasValidLocation =
              (locationType === "point" &&
                (stationForPreview || values.stationRegion)) ||
              (locationType === "area" &&
                (selectedGeofence.length > 0 ||
                  values.coordinates?.length > 0));

            return (
              <>
                <DrawerOverlay />
                <DrawerContent bg="white">
                  <DrawerCloseButton color={"black"} />
                  <DrawerHeader>
                    <Heading
                      display={"flex"}
                      size="md"
                      color="black"
                      gap="16px"
                    >
                      <MapPin size="24px" />
                      <Text mt={"3px"}>{t("editLocation")}</Text>
                    </Heading>
                  </DrawerHeader>
                  <DrawerBody color="black">
                    {isLoading ? (
                      <Center h="100%" w="100%" minH="400px">
                        <Spinner
                          speed="0.65s"
                          emptyColor="gray.300"
                          color="newBlue.500"
                          size="xl"
                        />
                      </Center>
                    ) : (
                      <Form onSubmit={handleSubmit}>
                        <VStack spacing={6} align="stretch">
                          {/* Name field */}
                          <FormControl
                            isRequired
                            isInvalid={!!errors.name && touched.name}
                          >
                            <FormLabel color="#414651" fontWeight={"500"}>
                              {t("locationName")}
                            </FormLabel>
                            <Input
                              name="name"
                              placeholder={
                                t("locationNamePlaceholder") as string
                              }
                              value={values.name}
                              onChange={handleChange}
                              maxLength={70}
                            />
                            <FormErrorMessage mt={4}>
                              {t("requiredField")}
                            </FormErrorMessage>
                          </FormControl>

                          {/* Location Type Selection */}
                          {showTypeSelection && (
                            <LocationTypeSelector
                              onSelect={handleLocationTypeSelect}
                              t={tOperationRule}
                            />
                          )}

                          {/* Point Selection */}
                          {locationType === "point" && !showTypeSelection && (
                            <Box borderWidth="1px" borderRadius="md" p={4}>
                              <Flex
                                gap="12px"
                                mb="16px"
                                justifyContent="space-between"
                              >
                                <Flex gap="12px">
                                  <MapPinned color="#086AA9" size="20px" />
                                  <Text
                                    fontWeight={"400"}
                                    fontSize="md"
                                    color="#086AA9"
                                  >
                                    {monitoredLocationTitle}
                                  </Text>
                                </Flex>
                                {/* Only show BackButton when creating new location, not editing */}
                                {!location && (
                                  <BackButton
                                    onClick={handleBackToTypeSelection}
                                    label={tOperationRule("back") as string}
                                  />
                                )}
                              </Flex>

                              <FormControl
                                isRequired
                                isInvalid={
                                  !!errors.coordinates && !!touched.coordinates
                                }
                              >
                                {stationForPreview ? (
                                  <MapPointPreview
                                    station={stationForPreview as any}
                                    onEdit={handleEditPoint}
                                    onClear={handleClear}
                                    t={t}
                                    hideSaveStationButton={true}
                                  />
                                ) : (
                                  // Show skeleton while redirecting to map for point selection
                                  <Box>
                                    <Skeleton
                                      height="20px"
                                      width="120px"
                                      mb={4}
                                    />
                                    <Skeleton
                                      height="200px"
                                      borderRadius="md"
                                    />
                                  </Box>
                                )}
                                <FormErrorMessage mt={4}>
                                  {t("pleaseSelectPoint")}
                                </FormErrorMessage>
                              </FormControl>
                            </Box>
                          )}

                          {/* Area Selection */}
                          {locationType === "area" && !showTypeSelection && (
                            <Box borderWidth="1px" borderRadius="md" p={4}>
                              <Flex
                                gap="12px"
                                mb="16px"
                                justifyContent="space-between"
                              >
                                <Flex gap="12px">
                                  <MapPinned color="#086AA9" size="20px" />
                                  <Text
                                    fontWeight={"400"}
                                    fontSize="md"
                                    color="#086AA9"
                                  >
                                    {monitoredLocationTitle}
                                  </Text>
                                </Flex>
                                {/* Only show BackButton when creating new location, not editing */}
                                {!location && (
                                  <BackButton
                                    onClick={handleBackToTypeSelection}
                                    label={tOperationRule("back") as string}
                                  />
                                )}
                              </Flex>

                              {/* Show saved area selector - enabled for editing */}
                              {values.savedLocationId && (
                                <FormControl mb={4}>
                                  <FormLabel color="#414651" fontWeight={"500"}>
                                    {t("area")}
                                  </FormLabel>
                                  <Box>
                                    <Select
                                      id="select-saved-area"
                                      showSearch
                                      optionFilterProp="children"
                                      placeholder={
                                        t("selectAreaPlaceholder") as string
                                      }
                                      value={values.savedLocationId}
                                      onChange={(selectedAreaId: number) => {
                                        handleAreaSelection(
                                          selectedAreaId,
                                          setFieldValue,
                                        );
                                      }}
                                      style={{
                                        width: "100%",
                                        height: "40px",
                                        borderColor: "#eff0f3",
                                      }}
                                      dropdownStyle={{ zIndex: 2000 }}
                                    >
                                      {locationStore.areas.map((area) => (
                                        <Select.Option
                                          value={area.id}
                                          key={area.id}
                                        >
                                          {area.name}
                                        </Select.Option>
                                      ))}
                                    </Select>
                                  </Box>
                                </FormControl>
                              )}

                              <FormControl
                                isRequired
                                isInvalid={
                                  !!errors.coordinates && !!touched.coordinates
                                }
                              >
                                <GeofenceMapSelector
                                  coordinates={values.coordinates}
                                  selectedGeofence={selectedGeofence}
                                  t={t}
                                  handleDrawingMode={
                                    handleDrawingModeWithFormValues
                                  }
                                  clearGeofence={handleClear}
                                  onCoordinatesChange={handleCoordinatesChange}
                                  formValues={{
                                    name: values.name,
                                    coordinatesType: "area",
                                    coordinates: values.coordinates || [],
                                  }}
                                  hideSaveAreaButtonOnMap={true}
                                  showSelectSavedAreaButton={false}
                                  isGeofenceInvalid={
                                    insightAlertStore.isGeofenceInvalid
                                  }
                                  isLocationLocked={!!values.savedLocationId}
                                />
                                <FormErrorMessage mt={4}>
                                  {t("pleaseDrawAnArea")}
                                </FormErrorMessage>
                              </FormControl>
                            </Box>
                          )}
                        </VStack>
                      </Form>
                    )}
                  </DrawerBody>

                  <DrawerFooter borderTopWidth="1px" bg="white">
                    <Button
                      variant="ghost"
                      color="newBlue.500"
                      mr={3}
                      onClick={handleClose}
                    >
                      {t("cancel")}
                    </Button>
                    <Button
                      layerStyle="ocean"
                      _hover={{ layerStyle: "ocean" }}
                      isLoading={isLoading}
                      loadingText={t("saveChanges") as string}
                      onClick={handleSubmitForm}
                      isDisabled={
                        insightAlertStore.isGeofenceInvalid ||
                        !hasValidLocation ||
                        showTypeSelection
                      }
                    >
                      {t("saveChanges")}
                    </Button>
                  </DrawerFooter>
                </DrawerContent>
              </>
            );
          }}
        </Formik>
      </Drawer>
    );
  },
);
